// PageDescription: 병원 상세 페이지
import { dehydrate } from '@tanstack/react-query';
import { GetServerSideProps } from 'next';
import { NextSeo } from 'next-seo';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import React, { useEffect } from 'react';

import DefaultLayout from '@/components/layouts/DefaultLayout';
import { FaceToFaceDdAction } from '@/constants/dd-actions/f2f';
import { EPixelEventName } from '@/constants/pixel';
import useHospitalDetailNoticePopupViewModel from '@/hooks/hospital/detail/useHospitalDetailNoticePopupViewModel';
import {
  useGetHospitalByIdQuery,
  useGetTotalReviewCountWithAverageRatingByPublicIdQuery,
  useHospitalExternalPostQuery,
} from '@/hooks/hospital/faceToFace.gql.impl';
import useGetHospitalById from '@/hooks/hospital/useGetHospitalById';
import useGetTotalReviewCountWithAverageRatingByPublicId from '@/hooks/hospital/useGetTotalReviewCountWithAverageRatingByPublicId';
import useHospitalExternalPostInfinityQuery from '@/hooks/hospital/useHospitalExternalPostInfinityQuery';
import useHospitalPageViewModel from '@/hooks/hospital/useHospitalPageViewModel';
import useLogger from '@/hooks/useLogger';
import { ExtendedQueryClient } from '@/models/common/query-client';
import { ssrQueryCache } from '@/models/common/ssr-cache';
import { parseRumClientType } from '@/parsers/common';
import { CustomPageProps } from '@/types/app';
import { ROUTES } from '@/types/common';
import { createHospitalLdJson } from '@/utils/format';

import ScrollRestorationDisabler from '@/components/common/scroll-restoration-disabler';
import HospitalNoticePopup from '@/components/templates/f2f/common/hospital-notice-popup';
import Pixel from '@/utils/pixel/pixel';

const HospitalDetailView = dynamic(() => import('@/views/hospitals/segments/ui/HospitalDetailView'));

interface Props extends CustomPageProps {
  hospitalPid: string;
}

const HospitalDetailPage = (props: Props) => {
  const { hospitalPid, client } = props;
  const router = useRouter();
  const { log } = useLogger();

  const viewModel = useHospitalPageViewModel(hospitalPid);
  const noticeViewModel = useHospitalDetailNoticePopupViewModel();
  const hospital = viewModel.hospital;

  const hospitalJson = createHospitalLdJson(viewModel.hospital, viewModel.reviewData, hospitalPid);

  const isPartnership = viewModel.hospitalPartnership.isPartnership();

  useEffect(() => {
    const type = Array.isArray(router.query.type) ? router.query.type.join(',') : router.query.type;

    Pixel.trackCustom(EPixelEventName.PageIn, {});

    log({
      eventName: FaceToFaceDdAction.PageViewHospitalDetail,
      payload: {
        type: type ?? 'default',
        partnership: viewModel.hospitalPartnership.getPartnershipAsString(),
        platform: parseRumClientType(client),
      },
    });
  }, []);

  return (
    <>
      <Head>
        <script type='application/ld+json' dangerouslySetInnerHTML={{ __html: JSON.stringify(hospitalJson) }} />
      </Head>
      <NextSeo
        title={`${hospital?.name} 위치 · 후기 · 예약 - 닥터나우`}
        description={`${
          hospital?.partnershipTemplate
            ? `${hospital?.regionByDepth.eupMyeonDongName} ${hospital?.name}에서, ${hospital.departmentGroups
                .slice(0, 3)
                .map((departmentGroup) => departmentGroup.departmentGroupName)
                .join(
                  ', '
                )} 진료를 바로 예약할 수 있어요. 76,614개 병원이 모인 닥터나우에서 내게 딱 맞는 병원을 찾아보세요!`
            : `${hospital?.regionByDepth.eupMyeonDongName} ${hospital?.name}에서, ${(hospital?.departmentsV2 ?? [])
                .slice(0, 3)
                .map((department) => department.departmentName)
                .join(
                  ', '
                )} 진료를 바로 예약할 수 있어요. 76,614개 병원이 모인 닥터나우에서 내게 딱 맞는 병원을 찾아보세요!`
        }`}
        canonical={`${process.env.NEXT_PUBLIC_DOMAIN}${ROUTES.HOSPITAL.DETAIL.path}${hospitalPid}`}
        openGraph={{
          url: `${process.env.NEXT_PUBLIC_DOMAIN}${ROUTES.HOSPITAL.DETAIL.path}${hospitalPid}`,
          title: `${hospital?.name} - 닥터나우`,
          description: '병원 위치부터 후기까지 확인 후, 예약해 보세요!',
          images: [
            {
              url: `${process.env.NEXT_PUBLIC_HOSPITAL_SEARCH_IMAGE}`,
              alt: 'Og Image Alt',
            },
          ],
        }}
        twitter={{
          handle: '@doctornow',
          site: '@doctornow.co.kr',
          cardType: 'summary_large_image',
        }}
      />
      <ScrollRestorationDisabler />
      <HospitalDetailView hospitalPid={hospitalPid} isPartnership={isPartnership} client={client} />
      <HospitalNoticePopup enabled={!client.isWebView} viewModel={noticeViewModel} />
    </>
  );
};

export default HospitalDetailPage;

HospitalDetailPage.Layout = DefaultLayout;

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const initialHospitalPid = ctx.params?.hospitalPid as string;
  const hospitalPid = initialHospitalPid?.toUpperCase();

  if (!hospitalPid) {
    return {
      redirect: {
        destination: ROUTES.HOSPITAL.HOME.path,
        permanent: true,
      },
    };
  }

  const queryClient = new ExtendedQueryClient();

  const params = { publicId: hospitalPid };

  const cacheKey = ctx.req.url as string;

  if (ssrQueryCache.has(cacheKey)) {
    queryClient.setQueryDataFromCache(ssrQueryCache.get(cacheKey) ?? []);

    return {
      props: {
        hospitalPid,
        dehydratedState: JSON.parse(JSON.stringify(dehydrate(queryClient))),
      },
    };
  }

  await Promise.allSettled([
    useGetHospitalById.prefetchQuery(queryClient, hospitalPid),
    useGetTotalReviewCountWithAverageRatingByPublicId.prefetchQuery(queryClient, hospitalPid),
    useHospitalExternalPostInfinityQuery.prefetchQuery(queryClient, {
      hospitalPublicId: hospitalPid,
    }),
  ]);

  ssrQueryCache.set(
    cacheKey,
    queryClient.getQueryClientData([
      useGetHospitalByIdQuery.getKey(params),
      useGetTotalReviewCountWithAverageRatingByPublicIdQuery.getKey(params),
      useHospitalExternalPostQuery.getKey({
        hospitalPublicId: hospitalPid,
        size: 3,
      }),
    ])
  );

  return {
    props: {
      hospitalPid,
      dehydratedState: JSON.parse(JSON.stringify(dehydrate(queryClient))),
    },
  };
};
